import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Plus, Search, Download, Printer, ChevronLeft, ArrowUpDown, QrCode, Eye, RefreshCw, MapPin } from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const RetailersContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();
  
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [activeFilter, setActiveFilter] = useState(searchParams.get("active") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchRetailers(); }, [page, activeFilter, selectedSite]);

  const fetchRetailers = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/retailers?page=${page}&limit=${limit}`;
      if (activeFilter !== "all") url += `&active=${activeFilter === "active"}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (selectedSite !== "all") url += `&site_id=${selectedSite}`;
      
      const res = await axios.get(url);
      setRetailers(res.data.retailers || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Retailer ID", "Name", "Contact Email", "Staff Pass Price", "Active", "Staff Passes", "Site", "Created"];
    const rows = retailers.map(r => [r.retailer_id, r.name, r.contact_email, r.staff_pass_price_display, r.active ? "Yes" : "No", r.staff_pass_count || 0, r.site_id || "", new Date(r.created_at).toLocaleDateString()]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `retailers-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-retailers">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Retailers</h1><p className="text-sm text-gray-500">{total} retailers</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
            <button onClick={() => navigate("/admin/commercial/retailers/new")} className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" /> Add Retailer</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchRetailers()} placeholder="Search retailers..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Staff Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Staff Passes</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={8} className="px-4 py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>)
              : retailers.length === 0 ? (<tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No retailers found</td></tr>)
              : retailers.map((retailer) => (
                <tr key={retailer.retailer_id} onClick={() => navigate(`/admin/commercial/retailers/${retailer.retailer_id}`)} className="hover:bg-blue-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-teal-600" /></div>
                      <div><p className="font-medium text-gray-900">{retailer.name}</p><p className="text-xs text-gray-500">{retailer.retailer_id}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{retailer.contact_email}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{retailer.staff_pass_price_display}</td>
                  <td className="px-4 py-3">
                    <span onClick={(e) => { e.stopPropagation(); navigate(`/admin/commercial/staff-passes?retailer_id=${retailer.retailer_id}`); }} className="text-sm text-blue-600 hover:underline cursor-pointer">{retailer.staff_pass_count || 0} passes</span>
                  </td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${retailer.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{retailer.active ? "Active" : "Inactive"}</span></td>
                  <td className="px-4 py-3">{retailer.site_id ? <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" /></span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(retailer.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => navigate(`/admin/commercial/retailers/${retailer.retailer_id}`)} className="p-2 hover:bg-gray-100 rounded-lg" title="View"><Eye className="w-4 h-4 text-gray-500" /></button>
                      <button onClick={() => navigate(`/admin/commercial/qr-centre?retailer=${retailer.retailer_id}`)} className="p-2 hover:bg-gray-100 rounded-lg" title="QR Code"><QrCode className="w-4 h-4 text-purple-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
};

export default function CommercialRetailers() {
  return (
    <CommercialProvider>
      <RetailersContent />
    </CommercialProvider>
  );
}
